import { useId, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import CanonicalTags from "@/components/seo/CanonicalTags";
import PublicOrgHeader from "@/components/org-story/PublicOrgHeader";
import PublicOrgFooter from "@/components/org-story/PublicOrgFooter";
import { PublicSignInProvider } from "@/components/org-story/publicSignIn";
import { StandalonePage, StandalonePanel } from "@/components/marketing/StandalonePage";
import { Skeleton } from "@/components/ui/skeleton";
import { typeLabel } from "@/components/explore/exploreShared";
import BrokerageDirectoryCard from "@/components/directory/BrokerageDirectoryCard";
import { DirectoryAccountPromptProvider } from "@/components/directory/DirectoryAccountPrompt";
import { useAuth } from "@/hooks/useAuth";
import { usePrerenderData } from "@/lib/prerenderData";
import { BROKERAGE_DIRECTORY_QUERY_KEY, loadBrokerageDirectory } from "@/lib/brokerageDirectoryData";
import {
  cityForSlug,
  DIRECTORY_BASE_PATH,
  DIRECTORY_CITY_PAGES,
  directoryCities,
  directoryCityPath,
  directoryTypes,
  filterDirectory,
  hasActiveFilters,
  type DirectoryEntry,
  type DirectoryFilters,
} from "@/lib/brokerageDirectory";

/**
 * The signed-out brokerage directory (#1050): /brokerages/sacramento and a
 * page per main city (/brokerages/sacramento/:city).
 *
 * Public and prerendered (src/routes/publicMarketingRoutes.tsx). The list is
 * built only from the anonymous public-profile RPCs (src/lib/brokerageDirectory.ts
 * says which keys); at build time scripts/prerender.mjs passes it in through
 * PrerenderDataProvider so the static HTML carries every card, and in the
 * browser it loads the build's snapshot, or the live RPCs without one
 * (src/lib/brokerageDirectoryData.ts).
 *
 * Loading, error and empty are distinct: a failed read shows an error with a
 * retry, never "no brokerages".
 */

const AREA_INTRO =
  "Real estate brokerages and teams across Sacramento, Placer, El Dorado, Yolo and Sutter counties.";

const ACCOUNT_LINE =
  "Profiles are free to read. A free agent account lets you compare brokerages side by side, see compensation models and fee structures, and start conversations.";

function headFor(city: string | null) {
  if (city) {
    return {
      title: `Real estate brokerages in ${city}, CA | Compaire`,
      description: `Browse real estate brokerages and teams in ${city}, California. Read their profiles free and compare them with a free agent account.`,
    };
  }
  return {
    title: "Real estate brokerages in greater Sacramento | Compaire",
    description:
      "Browse real estate brokerages and teams across Sacramento, Placer, El Dorado, Yolo and Sutter counties. Read their profiles free and compare them with a free agent account.",
  };
}

const NotFound = () => (
  <StandalonePage>
    <Helmet>
      <title>Page not found | Compaire</title>
      <meta name="robots" content="noindex" />
    </Helmet>
    <StandalonePanel
      tone="notice"
      title="Page not found"
      actions={
        <Link to={DIRECTORY_BASE_PATH} className="mk-btn mk-btn--outline">
          Brokerages in greater Sacramento
        </Link>
      }
    >
      <p className="mk-body">We don't have a directory page for that city.</p>
    </StandalonePanel>
  </StandalonePage>
);

export default function BrokerageDirectoryPage() {
  const { city: cityParam } = useParams<{ city?: string }>();
  const pageCity = cityForSlug(cityParam);
  if (cityParam !== undefined && !pageCity) return <NotFound />;
  return <DirectoryBody pageCity={pageCity} />;
}

function DirectoryBody({ pageCity }: { pageCity: string | null }) {
  const prerender = usePrerenderData();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const query = useQuery({
    queryKey: BROKERAGE_DIRECTORY_QUERY_KEY,
    queryFn: () => loadBrokerageDirectory(),
    initialData: prerender.brokerageDirectory,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  const [type, setType] = useState("");
  const [cloudOnly, setCloudOnly] = useState(false);
  const [search, setSearch] = useState("");

  // A main city has its own page; any other area city is ?city= on the base page.
  const city = pageCity ?? searchParams.get("city") ?? "";
  const filters: DirectoryFilters = { city, type, cloudOnly, query: search };

  const setCity = (next: string) => {
    if (!next) navigate(DIRECTORY_BASE_PATH);
    else if (DIRECTORY_CITY_PAGES.some((c) => c === next)) navigate(directoryCityPath(next));
    else navigate(`${DIRECTORY_BASE_PATH}?city=${encodeURIComponent(next)}`);
  };
  const clearFilters = () => {
    setType("");
    setCloudOnly(false);
    setSearch("");
    if (city) navigate(DIRECTORY_BASE_PATH);
  };

  const head = headFor(pageCity);
  const path = pageCity ? directoryCityPath(pageCity) : DIRECTORY_BASE_PATH;

  return (
    <PublicSignInProvider>
      <DirectoryAccountPromptProvider>
        <div className="mk-app flex min-h-screen flex-col">
          <PublicOrgHeader />
          <CanonicalTags path={path} title={head.title} description={head.description} />
          <main className="flex-1 pb-20">
            <div className="mx-auto max-w-[1140px] px-4 pt-6 sm:px-[30px]">
              <nav aria-label="Breadcrumb" className="mb-3 text-[13px] text-ink-500">
                <Link to="/teams-brokerages" className="hover:text-ink-900">
                  Teams &amp; Brokerages
                </Link>
                <span className="mx-2">/</span>
                {pageCity ? (
                  <>
                    <Link to={DIRECTORY_BASE_PATH} className="hover:text-ink-900">
                      Greater Sacramento
                    </Link>
                    <span className="mx-2">/</span>
                    <span className="text-ink-700">{pageCity}</span>
                  </>
                ) : (
                  <span className="text-ink-700">Greater Sacramento</span>
                )}
              </nav>

              <header className="max-w-[760px]">
                <h1 className="text-[28px] font-semibold leading-tight text-ink-900 sm:text-[36px]">
                  {pageCity ? `Real estate brokerages in ${pageCity}` : "Real estate brokerages in greater Sacramento"}
                </h1>
                <p className="mt-3 text-[15px] leading-relaxed text-ink-700">
                  {pageCity ? `Real estate brokerages and teams in ${pageCity}, California.` : AREA_INTRO}{" "}
                  {ACCOUNT_LINE}
                </p>
              </header>

              <DirectoryResults
                entries={query.data}
                isError={query.isError}
                isRetrying={query.isFetching}
                onRetry={() => void query.refetch()}
                filters={filters}
                setCity={setCity}
                setType={setType}
                setCloudOnly={setCloudOnly}
                setSearch={setSearch}
                clearFilters={clearFilters}
                signedIn={isAuthenticated}
              />

              <nav aria-label="Brokerages by city" className="mt-14 border-t border-border pt-6">
                <h2 className="text-[15px] font-semibold text-ink-900">Browse by city</h2>
                <ul className="mt-3 flex flex-wrap gap-x-5 gap-y-2 text-[14px]">
                  {DIRECTORY_CITY_PAGES.map((c) => (
                    <li key={c}>
                      <Link
                        to={directoryCityPath(c)}
                        aria-current={c === pageCity ? "page" : undefined}
                        className="text-ink-700 hover:text-primary hover:underline aria-[current=page]:font-semibold aria-[current=page]:text-ink-900"
                      >
                        {c}
                      </Link>
                    </li>
                  ))}
                </ul>
              </nav>
            </div>
          </main>
          <PublicOrgFooter />
        </div>
      </DirectoryAccountPromptProvider>
    </PublicSignInProvider>
  );
}

const fieldClass =
  "h-10 w-full rounded-md border border-input bg-card px-3 text-[14px] text-ink-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

function DirectoryResults({
  entries,
  isError,
  isRetrying,
  onRetry,
  filters,
  setCity,
  setType,
  setCloudOnly,
  setSearch,
  clearFilters,
  signedIn,
}: {
  entries: DirectoryEntry[] | undefined;
  isError: boolean;
  isRetrying: boolean;
  onRetry: () => void;
  filters: DirectoryFilters;
  setCity: (city: string) => void;
  setType: (type: string) => void;
  setCloudOnly: (value: boolean) => void;
  setSearch: (value: string) => void;
  clearFilters: () => void;
  signedIn: boolean;
}) {
  const ids = useId();
  const all = useMemo(() => entries ?? [], [entries]);
  const cities = useMemo(() => {
    const list = directoryCities(all);
    return filters.city && !list.includes(filters.city) ? [...list, filters.city].sort() : list;
  }, [all, filters.city]);
  const types = useMemo(() => directoryTypes(all), [all]);
  const anyCloud = all.some((e) => e.cloud_badge !== null);
  const shown = filterDirectory(all, filters);
  const owned = shown.filter((e) => e.listing_kind === "owned");
  const listed = shown.filter((e) => e.listing_kind === "listed");

  // Error with nothing to show: say so, offer a retry. Never "no brokerages".
  if (!entries && isError) {
    return (
      <div role="alert" className="mt-8 rounded-lg border border-border bg-card p-6">
        <h2 className="text-[17px] font-semibold text-ink-900">We couldn't load the directory</h2>
        <p className="mt-1 text-[14px] text-ink-700">Something went wrong on our side. Please try again.</p>
        <button
          type="button"
          onClick={onRetry}
          disabled={isRetrying}
          className="mt-4 inline-flex h-10 items-center rounded-md bg-primary px-4 text-[14px] font-medium text-primary-foreground disabled:opacity-60"
        >
          {isRetrying ? "Trying again…" : "Try again"}
        </button>
      </div>
    );
  }

  if (!entries) {
    return (
      <div role="status" aria-label="Loading brokerages" className="mt-8">
        <Skeleton className="h-10 w-full max-w-[760px]" />
        <ul className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" aria-hidden="true">
          {Array.from({ length: 6 }, (_, i) => (
            <li key={i}>
              <Skeleton className="h-[148px] w-full rounded-lg" />
            </li>
          ))}
        </ul>
      </div>
    );
  }

  if (all.length === 0) {
    return (
      <div className="mt-8 rounded-lg border border-border bg-card p-6">
        <h2 className="text-[17px] font-semibold text-ink-900">No brokerages are listed here yet</h2>
        <p className="mt-1 text-[14px] text-ink-700">
          We're adding greater Sacramento brokerages now. Check back soon, or{" "}
          <Link to="/teams-brokerages" className="text-primary hover:underline">
            list your brokerage
          </Link>
          .
        </p>
      </div>
    );
  }

  return (
    <>
      <form
        role="search"
        aria-label="Filter brokerages"
        className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)_minmax(0,1fr)_auto] lg:items-end"
        onSubmit={(e) => e.preventDefault()}
      >
        <div>
          <label htmlFor={`${ids}-q`} className="mb-1 block text-[13px] font-medium text-ink-700">
            Search by name
          </label>
          <input
            id={`${ids}-q`}
            type="search"
            value={filters.query}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Brokerage or team name"
            className={fieldClass}
          />
        </div>
        <div>
          <label htmlFor={`${ids}-city`} className="mb-1 block text-[13px] font-medium text-ink-700">
            City
          </label>
          <select id={`${ids}-city`} value={filters.city} onChange={(e) => setCity(e.target.value)} className={fieldClass}>
            <option value="">All cities</option>
            {cities.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor={`${ids}-type`} className="mb-1 block text-[13px] font-medium text-ink-700">
            Type
          </label>
          <select id={`${ids}-type`} value={filters.type} onChange={(e) => setType(e.target.value)} className={fieldClass}>
            <option value="">All types</option>
            {types.map((t) => (
              <option key={t} value={t}>
                {typeLabel(t)}
              </option>
            ))}
          </select>
        </div>
        {anyCloud && (
          <label className="flex h-10 items-center gap-2 text-[14px] text-ink-900">
            <input
              type="checkbox"
              checked={filters.cloudOnly}
              onChange={(e) => setCloudOnly(e.target.checked)}
              className="h-4 w-4 accent-primary"
            />
            Cloud brokerages only
          </label>
        )}
      </form>

      <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
        <p aria-live="polite" className="text-[14px] text-ink-500">
          {shown.length === 1 ? "1 brokerage" : `${shown.length} brokerages`}
        </p>
        {hasActiveFilters(filters) && (
          <button type="button" onClick={clearFilters} className="text-[14px] font-medium text-primary hover:underline">
            Clear filters
          </button>
        )}
      </div>

      {shown.length === 0 ? (
        <div className="mt-4 rounded-lg border border-border bg-card p-6">
          <h2 className="text-[17px] font-semibold text-ink-900">No brokerages match these filters</h2>
          <p className="mt-1 text-[14px] text-ink-700">Try another city or type, or clear the filters.</p>
        </div>
      ) : (
        <>
          {owned.length > 0 && (
            <section aria-labelledby={`${ids}-owned`} className="mt-4">
              <h2 id={`${ids}-owned`} className="sr-only">
                Brokerage profiles
              </h2>
              <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {owned.map((entry) => (
                  <BrokerageDirectoryCard key={entry.slug} entry={entry} signedIn={signedIn} />
                ))}
              </ul>
            </section>
          )}
          {listed.length > 0 && (
            <section aria-labelledby={`${ids}-listed`} className={owned.length > 0 ? "mt-10" : "mt-4"}>
              <h2 id={`${ids}-listed`} className="text-[17px] font-semibold text-ink-900">
                Basic listings
              </h2>
              <p className="mt-1 max-w-[760px] text-[14px] text-ink-700">
                These brokerages haven't completed a Compaire profile yet, so their pages show public business details only.
              </p>
              <ul className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {listed.map((entry) => (
                  <BrokerageDirectoryCard key={entry.slug} entry={entry} signedIn={signedIn} />
                ))}
              </ul>
            </section>
          )}
        </>
      )}
    </>
  );
}

